import React, { useMemo } from "react";
import { Skeleton, Stack } from "@mantine/core";
import { Alert, Badge, PageHeader, SectionCard } from "@xplatform/design-system";
import { IconAdjustments } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { VersionListItem } from "@models/mappingConfig";
import { AdminTable, col } from "@components/AdminTable";
import { useMappingServices } from "@hooks/mappingConfig";
import { mappingConfigRoutes } from "@utils/mappingConfig";

interface Row {
  service: string;
  plugin: VersionListItem | null;
}

/** Every mapping service in the registry and the active config of each of its plugins. */
export const MappingConfigListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const services = useMappingServices();

  const rows = useMemo<Row[]>(
    () =>
      (services.data?.items ?? []).flatMap((entry): Row[] =>
        entry.plugins.length > 0
          ? entry.plugins.map((plugin) => ({ service: entry.service, plugin }))
          : [{ service: entry.service, plugin: null }],
      ),
    [services.data],
  );

  const columns = [
    col.text<Row>("service", t("mappingConfig.columns.service"), (r) => r.service, { strong: true, width: "16%" }),
    col.text<Row>("plugin", t("mappingConfig.columns.plugin"), (r) => r.plugin?.plugin_id ?? null, { mono: true, width: "18%" }),
    col.twoLine<Row>("active", t("mappingConfig.columns.activeVersion"), (r) => ({
      primary: r.plugin?.version_id ?? t("mappingConfig.noConfig"),
      secondary: r.plugin?.comment || undefined,
    }), { mono: true }),
    col.text<Row>("model", t("mappingConfig.columns.model"), (r) => r.plugin?.model_name ?? null, { width: "18%" }),
    col.text<Row>("activatedBy", t("mappingConfig.columns.activatedBy"), (r) => r.plugin?.activated_by ?? null, { width: "14%" }),
    col.date<Row>("activatedAt", t("mappingConfig.columns.activatedAt"), (r) => r.plugin?.activated_at ?? null),
    col.badge<Row>("source", t("mappingConfig.columns.source"), (r) =>
      r.plugin?.source_kind ? <Badge variant="light" color="gray">{r.plugin.source_kind}</Badge> : "-"),
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title={t("mappingConfig.title")}
        description={t("mappingConfig.description")}
        icon={<IconAdjustments size={28} />}
      />
      {services.error && (
        <Alert color={services.forbidden ? "gray" : "red"} title={t("mappingConfig.loadFailed")}>{services.error}</Alert>
      )}
      <SectionCard>
        {services.loading && !services.data ? (
          <Stack gap="xs">{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} h={32} />)}</Stack>
        ) : (
          <AdminTable
            columns={columns}
            data={rows}
            getRowKey={(r) => `${r.service}/${r.plugin?.plugin_id ?? ""}`}
            onRowClick={(r) => r.plugin && navigate(mappingConfigRoutes.plugin(r.service, r.plugin.plugin_id))}
            emptyMessage={t("mappingConfig.noServices")}
          />
        )}
      </SectionCard>
    </Stack>
  );
};
